import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { format, addMonths } from "date-fns";
import { Plus, Minus } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { DateField } from "@/components/calculator/DateField";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import { monthsBetween, shiftMonths, monthsInRange } from "@/lib/calculators/monthCalculator";
import { gregorianToBangla } from "@/lib/bengaliDate";
import { gregorianToHijri } from "@/lib/calculators/dateCalculator";
import { BENGALI_MONTHS } from "@/data/bengaliMonths";
import { HIJRI_MONTHS } from "@/data/hijriMonths";

type B = { en: string; bn: string };
type Lang = "en" | "bn";
type TabId = "between" | "addsub" | "range";

const T = {
  h1: { en: "Month Calculator", bn: "মান্থ ক্যালকুলেটর" },
  desc: {
    en: "Calculate months between dates, add/subtract months, or list every month in a range — with Bengali (Bangla) and Hijri month context.",
    bn: "দুই তারিখের মধ্যে মাস, মাস যোগ/বিয়োগ, বা একটি রেঞ্জের সব মাস — বাংলা ও হিজরি মাস সহ।",
  },
  tBetween: { en: "Months between", bn: "মাসের ব্যবধান" },
  tAddSub: { en: "Add / Subtract months", bn: "মাস যোগ/বিয়োগ" },
  tRange: { en: "Months in range", bn: "রেঞ্জে মাসসমূহ" },
  start: { en: "Start date", bn: "শুরুর তারিখ" },
  end: { en: "End date", bn: "শেষের তারিখ" },
  startingDate: { en: "Starting date", bn: "শুরুর তারিখ" },
  monthsCount: { en: "Months", bn: "মাস" },
  add: { en: "Add", bn: "যোগ" },
  sub: { en: "Subtract", bn: "বিয়োগ" },
  bnDigits: { en: "Bengali numerals", bn: "বাংলা সংখ্যা" },
  bengali: { en: "Bengali", bn: "বাংলা" },
  hijri: { en: "Hijri", bn: "হিজরি" },
  preset: { en: "Presets", bn: "প্রিসেট" },
  banglaYear: { en: "Full Bangla year", bn: "পূর্ণ বাংলা বছর" },
  hijriYear: { en: "Full Hijri year", bn: "পূর্ণ হিজরি বছর" },
} as const;

const FAQ: { q: B; a: B }[] = [
  {
    q: { en: "How many months between two dates?", bn: "দুই তারিখের মধ্যে কত মাস?" },
    a: {
      en: "We count full calendar months from start to end first — i.e., the date in the end month that's on or after the start's day. The remainder becomes days. E.g., Jan 15 to Jun 11, 2026 = 4 months, 27 days (because Jun 11 is before Jun 15).",
      bn: "প্রথমে পূর্ণ মাস গণনা — অর্থাৎ শেষ মাসে শুরুর দিন পেরিয়েছে কিনা। অবশিষ্ট দিন। যেমন ১৫ জানু → ১১ জুন, ২০২৬ = ৪ মাস, ২৭ দিন।",
    },
  },
  {
    q: { en: "What happens when I add 1 month to Jan 31?", bn: "৩১ জানু-তে ১ মাস যোগ করলে?" },
    a: {
      en: "February doesn't have a 31st, so we clamp to the last day of the target month. Jan 31 + 1 month = Feb 28 (or Feb 29 in a leap year). The calculator marks this with a 'last day of month' note.",
      bn: "ফেব্রুয়ারিতে ৩১ তারিখ নেই, তাই মাসের শেষ দিনে ক্ল্যাম্প হয়। ৩১ জানু + ১ মাস = ২৮ ফেব্রু (লিপ ইয়ারে ২৯)। ক্যালকুলেটর এটি 'মাসের শেষ দিন' বলে চিহ্নিত করে।",
    },
  },
  {
    q: { en: "What are Bengali calendar months?", bn: "বাংলা মাসের নাম কী?" },
    a: {
      en: "There are 12 Bangla months: Boishakh, Joishtho, Ashar, Srabon, Bhadro, Ashwin, Kartik, Ogrohayon, Poush, Magh, Falgun, Choitro. The year starts on 14 April (Pohela Boishakh).",
      bn: "১২ মাস: বৈশাখ, জ্যৈষ্ঠ, আষাঢ়, শ্রাবণ, ভাদ্র, আশ্বিন, কার্তিক, অগ্রহায়ণ, পৌষ, মাঘ, ফাল্গুন, চৈত্র। বছর শুরু ১৪ এপ্রিল (পহেলা বৈশাখ)।",
    },
  },
  {
    q: { en: "What is the current Bengali month?", bn: "বর্তমান বাংলা মাস কী?" },
    a: {
      en: "Use any tab and pick today's date — the Bengali month appears next to the result. The current Bangla month depends on the day; e.g., May falls within Boishakh (until ~14 May) then Joishtho.",
      bn: "যেকোনো ট্যাবে আজকের তারিখ বসান — বাংলা মাস ফলাফলের পাশে দেখাবে। যেমন মে মাসের শুরুতে বৈশাখ, ১৫ মে থেকে জ্যৈষ্ঠ।",
    },
  },
  {
    q: { en: "What are Hijri (Islamic) calendar months?", bn: "হিজরি মাসের নাম কী?" },
    a: {
      en: "12 lunar months: Muharram, Safar, Rabi' al-Awwal, Rabi' al-Thani, Jumada al-Awwal, Jumada al-Thani, Rajab, Sha'ban, Ramadan, Shawwal, Dhu al-Qi'dah, Dhu al-Hijjah. Total ~354 days, so each Hijri month shifts ~11 days earlier each Gregorian year.",
      bn: "১২ চান্দ্র মাস: মুহাররম, সফর, রবিউল আউয়াল, রবিউস সানি, জমাদিউল আউয়াল, জমাদিউস সানি, রজব, শাবান, রমজান, শাওয়াল, জিলকদ, জিলহজ। মোট ~৩৫৪ দিন।",
    },
  },
  {
    q: { en: "What is the current Hijri month?", bn: "বর্তমান হিজরি মাস কী?" },
    a: {
      en: "Pick today's date in any tab — the Hijri month appears beside the result. Note: our calculation uses the tabular (Kuwaiti) algorithm; the official Bangladesh date may differ by ±1 day depending on moon sighting.",
      bn: "যেকোনো ট্যাবে আজকের তারিখ বসান — হিজরি মাস দেখাবে। আমরা ট্যাবুলার (কুয়েতি) অ্যালগরিদম ব্যবহার করি; চাঁদ দেখার ভিত্তিতে সরকারি তারিখ ±১ দিন ভিন্ন হতে পারে।",
    },
  },
  {
    q: { en: "Why does February show 28 or 29 days?", bn: "ফেব্রুয়ারি ২৮ না ২৯ দিন কেন?" },
    a: {
      en: "Leap years (every 4 years, except century years not divisible by 400) have 29 days in February. So 2024 had Feb 29; 2025, 2026, 2027 have Feb 28; 2028 has Feb 29 again.",
      bn: "লিপ ইয়ারে (প্রতি ৪ বছরে, শতকের যেগুলো ৪০০ দিয়ে বিভাজ্য নয় সেগুলো বাদে) ফেব্রুয়ারিতে ২৯ দিন। ২০২৪ লিপ; ২০২৫–২০২৭ নয়; ২০২৮ আবার লিপ।",
    },
  },
  {
    q: { en: "Can months be a decimal?", bn: "মাস কি দশমিক হতে পারে?" },
    a: {
      en: "Yes — total months as a decimal divides leftover days by the days in the post-anniversary month. Useful for prorated rent, contracts, and subscriptions billed monthly.",
      bn: "হ্যাঁ — অবশিষ্ট দিনকে সংশ্লিষ্ট মাসের মোট দিন দিয়ে ভাগ করে দশমিক মাস পাওয়া যায়। প্রোরেটেড ভাড়া, চুক্তি ইত্যাদিতে কাজে লাগে।",
    },
  },
];

export const Route = createFileRoute("/month-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Month Calculator — Bengali & Hijri Months",
      description:
        "Free month calculator. Calculate months between dates, add/subtract months. Bengali calendar months (বৈশাখ, জ্যৈষ্ঠ…) and Hijri months. মান্থ ক্যালকুলেটর।",
      path: "/month-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Month Calculator", path: "/month-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: MonthCalculatorPage,
});

const HASH_TO_TAB: Record<string, TabId> = { between: "between", addsub: "addsub", range: "range" };

function MonthCalculatorPage() {
  const lang = useAppStore((s) => s.lang) as Lang;
  const tr = (k: keyof typeof T) => (T[k] as B)[lang];
  const [bnDigits, setBnDigits] = useState(false);
  const useBn = lang === "bn" || bnDigits;
  const num = (n: number | string) => (useBn ? toBengaliNumerals(n) : String(n));
  const fmtN = (n: number) => num(n.toLocaleString("en-US"));
  const fmtDate = (d: Date) => {
    const s = format(d, "d MMM yyyy");
    return useBn ? toBengaliNumerals(s) : s;
  };

  const [tab, setTab] = useState<TabId>("between");
  useEffect(() => {
    if (typeof window === "undefined") return;
    const h = window.location.hash.replace("#", "");
    if (HASH_TO_TAB[h]) setTab(HASH_TO_TAB[h]);
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash.replace("#", "") !== tab) {
      window.history.replaceState(null, "", `#${tab}`);
    }
  }, [tab]);

  const today = useMemo(() => new Date(), []);
  const [start, setStart] = useState<Date | null>(new Date(today.getFullYear(), 0, 15));
  const [end, setEnd] = useState<Date | null>(new Date(today.getFullYear(), 5, 11));
  const [base, setBase] = useState<Date | null>(today);
  const [op, setOp] = useState<"add" | "sub">("add");
  const [nStr, setNStr] = useState("6");
  const [rangeStart, setRangeStart] = useState<Date | null>(new Date(today.getFullYear(), 0, 1));
  const [rangeEnd, setRangeEnd] = useState<Date | null>(new Date(today.getFullYear(), 3, 30));

  const between = useMemo(() => (start && end ? monthsBetween(start, end) : null), [start, end]);

  const addsub = useMemo(() => {
    const n = parseInt(nStr, 10);
    if (!base || !Number.isFinite(n)) return null;
    return shiftMonths(base, n, op);
  }, [base, op, nStr]);

  const range = useMemo(() => (rangeStart && rangeEnd ? monthsInRange(rangeStart, rangeEnd) : []), [rangeStart, rangeEnd]);

  const startBangla = start ? gregorianToBangla(start) : null;
  const endBangla = end ? gregorianToBangla(end) : null;
  const startHijri = start ? gregorianToHijri(start) : null;
  const endHijri = end ? gregorianToHijri(end) : null;

  const setBanglaYearPreset = () => {
    const y = today.getFullYear();
    const isAfterPB = today.getMonth() > 3 || (today.getMonth() === 3 && today.getDate() >= 14);
    const startY = isAfterPB ? y : y - 1;
    setRangeStart(new Date(startY, 3, 14));
    setRangeEnd(new Date(startY + 1, 3, 13));
  };
  const setHijriYearPreset = () => {
    setRangeStart(today);
    setRangeEnd(addMonths(today, 12));
  };

  return (
    <CalculatorLayout
      slug="month-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="CalendarRange"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      <div className="mb-6 flex flex-wrap items-center justify-end gap-3">
        <label className={cn("inline-flex items-center gap-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
          <input type="checkbox" checked={bnDigits} onChange={(e) => setBnDigits(e.target.checked)} className="h-3.5 w-3.5 accent-accent" />
          {tr("bnDigits")}
        </label>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabId)}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="between" className={cn(lang === "bn" && "font-bn")}>{tr("tBetween")}</TabsTrigger>
          <TabsTrigger value="addsub" className={cn(lang === "bn" && "font-bn")}>{tr("tAddSub")}</TabsTrigger>
          <TabsTrigger value="range" className={cn(lang === "bn" && "font-bn")}>{tr("tRange")}</TabsTrigger>
        </TabsList>

        {/* Tab 1 */}
        <TabsContent value="between" className="mt-6 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <DateField label={tr("start")} value={start} onChange={setStart} fromYear={1900} toDate={new Date(2100, 11, 31)} />
            <DateField label={tr("end")} value={end} onChange={setEnd} fromYear={1900} toDate={new Date(2100, 11, 31)} />
          </div>
          {between && start && end && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
              <div className="rounded-2xl border border-accent/40 bg-accent/5 p-5 text-center">
                <p className="text-3xl font-bold tabular-nums md:text-4xl">
                  {fmtN(between.months)} <span className="text-lg text-muted-foreground">{lang === "en" ? "months" : "মাস"}</span>
                  {between.days > 0 && (
                    <>, {fmtN(between.days)} <span className="text-lg text-muted-foreground">{lang === "en" ? "days" : "দিন"}</span></>
                  )}
                </p>
                <p className={cn("mt-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
                  {fmtDate(start)} → {fmtDate(end)}
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <Stat label={lang === "en" ? "Decimal months" : "দশমিক মাস"} value={num(between.totalMonthsDecimal.toFixed(2))} />
                {startBangla && endBangla && (
                  <Stat
                    label={tr("bengali")}
                    value={`${startBangla.monthBn} → ${endBangla.monthBn}`}
                  />
                )}
                {startHijri && endHijri && (
                  <Stat
                    label={tr("hijri")}
                    value={`${startHijri.monthEn} → ${endHijri.monthEn}`}
                  />
                )}
              </div>
            </motion.div>
          )}
        </TabsContent>

        {/* Tab 2 */}
        <TabsContent value="addsub" className="mt-6 space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <DateField label={tr("startingDate")} value={base} onChange={setBase} fromYear={1900} toDate={new Date(2100, 11, 31)} />
            <div className="space-y-1.5">
              <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>{lang === "en" ? "Operation" : "অপারেশন"}</label>
              <div className="flex gap-2">
                {(["add", "sub"] as const).map((o) => (
                  <button
                    key={o}
                    type="button"
                    onClick={() => setOp(o)}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-xs font-medium",
                      op === o ? "border-accent bg-accent text-accent-foreground" : "border-border bg-card hover:border-accent/40",
                      lang === "bn" && "font-bn",
                    )}
                  >
                    {o === "add" ? <Plus size={12} /> : <Minus size={12} />}
                    {o === "add" ? tr("add") : tr("sub")}
                  </button>
                ))}
              </div>
            </div>
            <NumField label={tr("monthsCount")} value={nStr} onChange={setNStr} useBn={useBn} lang={lang} />
          </div>

          {addsub && base && (
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
              <div className="rounded-2xl border border-accent/40 bg-accent/5 p-5 text-center">
                <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
                  {fmtDate(base)} {op === "add" ? "+" : "−"} {num(nStr)} {tr("monthsCount")} =
                </p>
                <p className="mt-1 text-3xl font-bold tabular-nums md:text-4xl">{fmtDate(addsub.result)}</p>
                {addsub.clamped && (
                  <p className={cn("mt-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
                    {lang === "en" ? "(last day of month — original day didn't exist in the target month)" : "(মাসের শেষ দিন — মূল তারিখ লক্ষ্য মাসে নেই)"}
                  </p>
                )}
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <Stat label={tr("bengali")} value={`${gregorianToBangla(addsub.result).monthBn} ${num(gregorianToBangla(addsub.result).year)}`} />
                <Stat label={tr("hijri")} value={`${gregorianToHijri(addsub.result).monthEn} ${num(gregorianToHijri(addsub.result).year)}`} />
              </div>
            </motion.div>
          )}
        </TabsContent>

        {/* Tab 3 */}
        <TabsContent value="range" className="mt-6 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <DateField label={tr("start")} value={rangeStart} onChange={setRangeStart} fromYear={1900} toDate={new Date(2100, 11, 31)} />
            <DateField label={tr("end")} value={rangeEnd} onChange={setRangeEnd} fromYear={1900} toDate={new Date(2100, 11, 31)} />
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className={cn(lang === "bn" && "font-bn")}>{tr("preset")}:</span>
            <button type="button" onClick={setBanglaYearPreset} className={cn("rounded-full border border-border bg-card px-2.5 py-1 hover:border-accent", lang === "bn" && "font-bn")}>
              {tr("banglaYear")}
            </button>
            <button type="button" onClick={setHijriYearPreset} className={cn("rounded-full border border-border bg-card px-2.5 py-1 hover:border-accent", lang === "bn" && "font-bn")}>
              {tr("hijriYear")}
            </button>
          </div>

          {range.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="p-2 text-left">{lang === "en" ? "Gregorian" : "গ্রেগরীয়"}</th>
                    <th className="p-2 text-left">{tr("bengali")}</th>
                    <th className="p-2 text-left">{tr("hijri")}</th>
                    <th className="p-2 text-right">{lang === "en" ? "Days" : "দিন"}</th>
                  </tr>
                </thead>
                <tbody>
                  {range.map((m) => (
                    <tr key={m.label} className="border-t border-border align-top">
                      <td className="p-2 font-medium">{useBn ? toBengaliNumerals(m.label) : m.label}</td>
                      <td className={cn("p-2 text-muted-foreground", "font-bn")}>
                        {m.banglaSpan.early.monthBn === m.banglaSpan.late.monthBn
                          ? `${m.banglaSpan.early.monthBn} ${num(m.banglaSpan.early.year)}`
                          : `${m.banglaSpan.early.monthBn}–${m.banglaSpan.late.monthBn} ${num(m.banglaSpan.late.year)}`}
                      </td>
                      <td className="p-2 text-muted-foreground">
                        {m.hijriSpan.early.monthEn === m.hijriSpan.late.monthEn
                          ? `${m.hijriSpan.early.monthEn} ${num(m.hijriSpan.early.year)}`
                          : `${m.hijriSpan.early.monthEn}–${m.hijriSpan.late.monthEn} ${num(m.hijriSpan.late.year)}`}
                      </td>
                      <td className="p-2 text-right tabular-nums">{num(m.daysInMonth)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-accent/5">
                  <tr className="border-t-2 border-accent/30 font-semibold">
                    <td className="p-2" colSpan={3}>{lang === "en" ? "Total months" : "মোট মাস"}</td>
                    <td className="p-2 text-right tabular-nums">{num(range.length)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Educational content */}
      <article className="mt-12 space-y-10">
        <Section title={lang === "en" ? "Bengali calendar months — full guide" : "বাংলা মাস — পূর্ণ গাইড"} lang={lang}>
          <div className="grid gap-3 md:grid-cols-2">
            {BENGALI_MONTHS.map((m) => (
              <div key={m.index} className="rounded-xl border border-border bg-card p-3">
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="font-bn text-base font-semibold">
                    {num(m.index)}. {m.bn} <span className="text-xs font-normal text-muted-foreground">({m.en})</span>
                  </h3>
                  <span className="text-xs text-muted-foreground">{m.gregorianRange}</span>
                </div>
                <p className={cn("mt-1 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>{m.significance[lang]}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section title={lang === "en" ? "Hijri (Islamic) calendar months — full guide" : "হিজরি মাস — পূর্ণ গাইড"} lang={lang}>
          <div className="grid gap-3 md:grid-cols-2">
            {HIJRI_MONTHS.map((m) => (
              <div key={m.index} className="rounded-xl border border-border bg-card p-3">
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="text-base font-semibold">
                    {num(m.index)}. {m.en} <span className="text-xs font-normal text-muted-foreground">({m.arabic})</span>
                  </h3>
                  <span className="font-bn text-xs text-muted-foreground">{m.bn}</span>
                </div>
                <p className={cn("mt-1 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>{m.significance[lang]}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section title={lang === "en" ? "Edge cases: month-end & leap years" : "বিশেষ ক্ষেত্র: মাসের শেষ ও লিপ ইয়ার"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "en"
              ? "When you add months to a date like Jan 31, the target month (February) doesn't have a 31st. We clamp to the last day — Feb 28 or Feb 29 in a leap year. Same logic for Mar 31 + 1 month → Apr 30. Date-fns handles this and we mark the result with a 'last day of month' note."
              : "৩১ জানু-এর মতো তারিখে মাস যোগ করলে লক্ষ্য মাসে (ফেব্রুয়ারি) ৩১ তারিখ নেই। আমরা শেষ দিনে ক্ল্যাম্প করি — ২৮/২৯ ফেব্রু। ৩১ মার্চ + ১ মাস → ৩০ এপ্রিল-ও একই নিয়ম।"}
          </p>
        </Section>

        <Section title={lang === "en" ? "Related calculators" : "সম্পর্কিত ক্যালকুলেটর"} lang={lang}>
          <div className="flex flex-wrap gap-2 text-sm">
            <Link to="/date-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Date" : "তারিখ"}</Link>
            <Link to="/year-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Year" : "বছর"}</Link>
            <Link to="/days-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Days" : "দিন"}</Link>
            <Link to="/age-calculator" className="rounded-md border border-border px-3 py-1.5 hover:border-accent hover:text-accent">{lang === "en" ? "Age" : "বয়স"}</Link>
          </div>
        </Section>
      </article>
    </CalculatorLayout>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-base font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function NumField({
  label, value, onChange, lang, useBn,
}: { label: string; value: string; onChange: (v: string) => void; lang: Lang; useBn: boolean }) {
  const display = useBn ? toBengaliNumerals(value) : value;
  return (
    <div className="space-y-1.5">
      <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>{label}</label>
      <input
        type="text"
        inputMode="numeric"
        value={display}
        onChange={(e) =>
          onChange(
            e.target.value
              .replace(/[০-৯]/g, (d) => String("০১২৩৪৫৬৭৮৯".indexOf(d)))
              .replace(/[^0-9-]/g, ""),
          )
        }
        className="w-full rounded-lg border border-border bg-muted px-3 py-2.5 text-base tabular-nums outline-none focus:border-accent focus:bg-background focus:ring-2 focus:ring-accent/20"
      />
    </div>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: Lang }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
